<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import PipelinesAPI from 'dashboard/api/pipelines';
import Button from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Modal from 'dashboard/components/Modal.vue';
import { emitter } from 'shared/helpers/mitt';
import MessageTemplatesHeader from './MessageTemplatesHeader.vue';
import MessageTemplateForm from './MessageTemplateForm.vue';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { normalizeLocale } from '../utils/localeFormatting';
import { serializeMessageTemplate } from '../helpers/messageTemplateSerializer';

import { REPORTING_DEFAULT_TIMEZONE } from 'dashboard/helper/reportingTimezone';
const emit = defineEmits(['switch-view']);
const { locale, t } = useI18n();
const store = useStore();

const loading = ref(false);
const pipelines = ref([]);
const hasLoadError = ref(false);
const selectedPipelineId = ref('');
const selectedCategory = ref('all');
const searchQuery = ref('');
const activeRequest = ref(0);
const isMounted = ref(false);

const showDeleteModal = ref(false);
const templateToDelete = ref(null);
const stageToDeleteFrom = ref('');
const isDeleting = ref(false);
const busyTemplateKey = ref('');

const showEditModal = ref(false);
const templateToEdit = ref(null);
const editFormRef = ref(null);
const editFormDirty = ref(false);

const showCreateModal = ref(false);
const newTemplateData = ref(null);
const createFormRef = ref(null);
const createFormDirty = ref(false);

const showPreviewModal = ref(false);
const templateToPreview = ref(null);

const clone = value => JSON.parse(JSON.stringify(value));
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const currentAccountId = computed(
  () => store.getters.getCurrentAccountId ?? currentAccount.value.id ?? null
);
let accountGeneration = 0;
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const captureAccountContext = () => ({
  accountId: normalizedAccountId(),
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  context.accountId !== null &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;
const reportingTimezone = computed(
  () =>
    currentAccount.value.reporting_timezone ||
    currentAccount.value.settings?.reporting_timezone ||
    REPORTING_DEFAULT_TIMEZONE
);
const activeLocale = computed(() => normalizeLocale(locale.value));

const categories = computed(() => [
  {
    id: 'all',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.ALL'),
    icon: 'globe',
  },
  {
    id: 'follow-up',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.FOLLOW_UP'),
    icon: 'mail',
  },
  {
    id: 'proposal',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.PROPOSAL'),
    icon: 'document',
  },
  {
    id: 'negotiation',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.NEGOTIATION'),
    icon: 'people',
  },
  {
    id: 'closing',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.CLOSING'),
    icon: 'checkmark-circle',
  },
  {
    id: 'support',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.SUPPORT'),
    icon: 'chat-help',
  },
  {
    id: 'other',
    label: t('PIPELINE.MESSAGE_TEMPLATES.CATEGORIES.OTHER'),
    icon: 'more-horizontal',
  },
]);

const pipelineOptions = computed(() =>
  pipelines.value.map(pipeline => ({
    value: String(pipeline.id),
    label: pipeline.name,
  }))
);

const extractVariables = content => {
  const matches = String(content || '').match(/\{([^}]+)\}/g);
  return matches ? [...new Set(matches.map(match => match.slice(1, -1)))] : [];
};

const selectedPipeline = computed(() => {
  const pipeline = pipelines.value.find(
    item => String(item.id) === String(selectedPipelineId.value)
  );
  if (!pipeline) return null;

  return {
    ...pipeline,
    stages: Object.entries(pipeline.stages || {})
      .map(([id, stage]) => ({
        id: String(id),
        ...stage,
        message_templates: (Array.isArray(stage.message_templates)
          ? stage.message_templates
          : []
        )
          .filter(template => template && typeof template === 'object')
          .map(template => ({
            ...template,
            category: template.category || 'other',
            variables: extractVariables(template.content),
          })),
      }))
      .sort(
        (left, right) =>
          (Number.parseInt(left.position, 10) || 0) -
          (Number.parseInt(right.position, 10) || 0)
      ),
  };
});
const { canManagePipeline } = usePipelinePermissions(selectedPipeline);
const canMutateSelectedPipeline = pipelineId =>
  canManagePipeline.value &&
  String(selectedPipeline.value?.id ?? '') === String(pipelineId ?? '');

const allTemplates = computed(() =>
  (selectedPipeline.value?.stages || []).flatMap(stage =>
    stage.message_templates.map(template => ({
      ...template,
      stageName: stage.name,
      stageId: stage.id,
      stageColor: stage.color,
    }))
  )
);

const filteredTemplates = computed(() => {
  const normalizedQuery = searchQuery.value.trim().toLocaleLowerCase();
  return allTemplates.value.filter(template => {
    const matchesCategory =
      selectedCategory.value === 'all' ||
      template.category === selectedCategory.value;
    const matchesSearch =
      !normalizedQuery ||
      String(template.title || '')
        .toLocaleLowerCase()
        .includes(normalizedQuery) ||
      String(template.content || '')
        .toLocaleLowerCase()
        .includes(normalizedQuery);
    return matchesCategory && matchesSearch;
  });
});

const categoryCount = computed(() => {
  const counts = { all: allTemplates.value.length };
  categories.value.slice(1).forEach(category => {
    counts[category.id] = allTemplates.value.filter(
      template => template.category === category.id
    ).length;
  });
  return counts;
});

const fetchPipelines = async () => {
  if (loading.value) return;

  const accountContext = captureAccountContext();
  const requestId = activeRequest.value + 1;
  activeRequest.value = requestId;
  const requestIsCurrent = () =>
    isMounted.value &&
    requestId === activeRequest.value &&
    accountContextIsCurrent(accountContext);
  if (accountContext.accountId === null) {
    pipelines.value = [];
    hasLoadError.value = true;
    return;
  }

  loading.value = true;
  hasLoadError.value = false;
  try {
    const response = await PipelinesAPI.get(accountContext.accountId);
    if (!requestIsCurrent()) return;

    pipelines.value = Array.isArray(response.data) ? response.data : [];
    const selectedStillExists = pipelines.value.some(
      pipeline => String(pipeline.id) === String(selectedPipelineId.value)
    );
    if (!selectedStillExists) {
      selectedPipelineId.value = pipelines.value[0]
        ? String(pipelines.value[0].id)
        : '';
    }
  } catch {
    if (!requestIsCurrent()) return;
    hasLoadError.value = true;
  } finally {
    if (requestIsCurrent()) {
      loading.value = false;
    }
  }
};

const refreshAfterSave = async () => {
  await fetchPipelines();
};

const clearFilters = () => {
  searchQuery.value = '';
  selectedCategory.value = 'all';
};

const getTemplateKey = template =>
  String(template.stageId) + ':' + String(template.id);

const openPreview = template => {
  templateToPreview.value = template;
  showPreviewModal.value = true;
};

const handleEditTemplate = (template, stageId) => {
  if (!canManagePipeline.value) return;

  templateToEdit.value = {
    ...template,
    pipeline_id: String(selectedPipelineId.value),
    stage_id: String(stageId),
  };
  editFormDirty.value = false;
  showEditModal.value = true;
};

const openCreateModal = () => {
  if (!canManagePipeline.value) return;

  const firstStage = selectedPipeline.value?.stages[0];
  newTemplateData.value = {
    pipeline_id: String(selectedPipelineId.value || ''),
    stage_id: firstStage?.id || '',
  };
  createFormDirty.value = false;
  showCreateModal.value = true;
};

const closeEditModal = () => {
  editFormDirty.value = false;
  showEditModal.value = false;
  templateToEdit.value = null;
};

const closeCreateModal = () => {
  createFormDirty.value = false;
  showCreateModal.value = false;
  newTemplateData.value = null;
};

const editModalModel = computed({
  get: () => showEditModal.value,
  set: value => {
    if (value) {
      if (canManagePipeline.value) showEditModal.value = true;
      return;
    }
    if (!editFormDirty.value) {
      closeEditModal();
      return;
    }
    editFormRef.value?.requestClose();
  },
});

const createModalModel = computed({
  get: () => showCreateModal.value,
  set: value => {
    if (value) {
      if (canManagePipeline.value) showCreateModal.value = true;
      return;
    }
    if (!createFormDirty.value) {
      closeCreateModal();
      return;
    }
    createFormRef.value?.requestClose();
  },
});

const handleTemplateSaved = async () => {
  closeEditModal();
  await refreshAfterSave();
};

const handleNewTemplateCreated = async () => {
  closeCreateModal();
  await refreshAfterSave();
};

const createTemplateId = templates => {
  let candidate = Date.now();
  const ids = new Set(templates.map(template => String(template?.id)));
  while (ids.has(String(candidate))) candidate += 1;
  return candidate;
};

const handleDuplicateTemplate = async template => {
  const templateKey = getTemplateKey(template);
  const mutationPipelineId = selectedPipeline.value?.id;
  if (!canMutateSelectedPipeline(mutationPipelineId) || busyTemplateKey.value) {
    return;
  }

  const pipeline = pipelines.value.find(
    item => String(item.id) === String(mutationPipelineId)
  );
  const updatedStages = clone(pipeline?.stages || {});
  const stage = updatedStages[template.stageId];
  if (!pipeline || !stage) return;
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;

  busyTemplateKey.value = templateKey;
  try {
    const templates = Array.isArray(stage.message_templates)
      ? stage.message_templates
      : [];
    const duplicated = serializeMessageTemplate({
      ...template,
      id: createTemplateId(templates),
      title: t('PIPELINE.MESSAGE_TEMPLATES.DUPLICATE_TITLE', {
        title: template.title,
      }),
      created_at: new Date().toISOString(),
      updated_at: undefined,
    });
    stage.message_templates = [...templates, duplicated];
    if (!canMutateSelectedPipeline(mutationPipelineId)) return;
    await PipelinesAPI.update(
      pipeline.id,
      { stages: updatedStages },
      accountContext.accountId
    );
    if (
      !accountContextIsCurrent(accountContext) ||
      !canMutateSelectedPipeline(mutationPipelineId)
    ) {
      return;
    }
    await fetchPipelines();
    if (
      !accountContextIsCurrent(accountContext) ||
      !canMutateSelectedPipeline(mutationPipelineId)
    ) {
      return;
    }
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGE_TEMPLATES.DUPLICATE_SUCCESS'),
      action: { type: 'success' },
    });
  } catch {
    if (!accountContextIsCurrent(accountContext)) return;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGE_TEMPLATES.DUPLICATE_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (accountContextIsCurrent(accountContext)) busyTemplateKey.value = '';
  }
};

const confirmDelete = template => {
  if (!canManagePipeline.value) return;

  templateToDelete.value = {
    ...template,
    pipelineId: selectedPipeline.value?.id,
  };
  stageToDeleteFrom.value = template.stageId;
  showDeleteModal.value = true;
};

const resetDeleteState = () => {
  if (isDeleting.value) return;
  showDeleteModal.value = false;
  templateToDelete.value = null;
  stageToDeleteFrom.value = '';
};

const handleDeleteTemplate = async () => {
  const mutationPipelineId = templateToDelete.value?.pipelineId;
  if (
    !canMutateSelectedPipeline(mutationPipelineId) ||
    isDeleting.value ||
    !templateToDelete.value
  ) {
    return;
  }

  const pipeline = pipelines.value.find(
    item => String(item.id) === String(mutationPipelineId)
  );
  const updatedStages = clone(pipeline?.stages || {});
  const stage = updatedStages[stageToDeleteFrom.value];
  if (!pipeline || !stage) return;
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;

  isDeleting.value = true;
  try {
    const templateId = String(templateToDelete.value.id);
    stage.message_templates = (
      Array.isArray(stage.message_templates) ? stage.message_templates : []
    ).filter(template => String(template?.id) !== templateId);
    if (!canMutateSelectedPipeline(mutationPipelineId)) return;
    await PipelinesAPI.update(
      pipeline.id,
      { stages: updatedStages },
      accountContext.accountId
    );
    if (
      !accountContextIsCurrent(accountContext) ||
      !canMutateSelectedPipeline(mutationPipelineId)
    ) {
      return;
    }
    showDeleteModal.value = false;
    templateToDelete.value = null;
    stageToDeleteFrom.value = '';
    await fetchPipelines();
    if (
      !accountContextIsCurrent(accountContext) ||
      !canMutateSelectedPipeline(mutationPipelineId)
    ) {
      return;
    }
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGE_TEMPLATES.DELETE_SUCCESS'),
      action: { type: 'success' },
    });
  } catch {
    if (!accountContextIsCurrent(accountContext)) return;
    emitter.emit('newToastMessage', {
      message: t('PIPELINE.MESSAGE_TEMPLATES.DELETE_ERROR'),
      action: { type: 'error' },
    });
  } finally {
    if (accountContextIsCurrent(accountContext)) isDeleting.value = false;
  }
};

const getCategory = category =>
  categories.value.find(item => item.id === category) ||
  categories.value.find(item => item.id === 'other');

const getCategoryColor = category => {
  const colors = {
    'follow-up': 'text-n-blue-11 bg-n-blue-3',
    proposal: 'text-n-violet-11 bg-n-violet-3',
    negotiation: 'text-n-amber-11 bg-n-amber-3',
    closing: 'text-n-teal-11 bg-n-teal-3',
    support: 'text-n-ruby-11 bg-n-ruby-3',
    other: 'text-n-slate-11 bg-n-slate-3',
  };
  return colors[category] || colors.other;
};

const formatDate = value => {
  if (!value) return t('PIPELINE.MESSAGE_TEMPLATES.DATE_UNKNOWN');
  try {
    return new Intl.DateTimeFormat(activeLocale.value, {
      dateStyle: 'medium',
      timeZone: reportingTimezone.value,
    }).format(new Date(value));
  } catch {
    return t('PIPELINE.MESSAGE_TEMPLATES.DATE_UNKNOWN');
  }
};

onMounted(() => {
  isMounted.value = true;
  fetchPipelines();
});

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    activeRequest.value += 1;
    loading.value = false;
    pipelines.value = [];
    selectedPipelineId.value = '';
    busyTemplateKey.value = '';
    isDeleting.value = false;
    closeEditModal();
    closeCreateModal();
    resetDeleteState();
    if (isMounted.value) fetchPipelines();
  },
  { flush: 'sync' }
);

watch(selectedPipelineId, (pipelineId, previousPipelineId) => {
  if (String(pipelineId) === String(previousPipelineId)) return;
  busyTemplateKey.value = '';
  isDeleting.value = false;
  closeEditModal();
  closeCreateModal();
  resetDeleteState();
});

onBeforeUnmount(() => {
  isMounted.value = false;
  activeRequest.value += 1;
});
</script>

<template>
  <div class="flex h-full w-full flex-col bg-n-background">
    <MessageTemplatesHeader
      :can-manage="canManagePipeline"
      @switch-view="view => emit('switch-view', view)"
      @add-template="openCreateModal"
    />

    <main class="min-h-0 flex-1 overflow-auto p-4 sm:p-6">
      <div
        v-if="loading"
        role="status"
        aria-live="polite"
        class="flex h-full min-h-64 items-center justify-center gap-3 text-sm text-n-slate-11"
      >
        <span
          aria-hidden="true"
          class="size-7 animate-spin rounded-full border-2 border-n-weak border-t-n-brand motion-reduce:animate-none"
        />
        {{ t('PIPELINE.MESSAGE_TEMPLATES.LOADING') }}
      </div>

      <div
        v-else-if="hasLoadError"
        role="alert"
        class="flex h-full min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-6 text-center"
      >
        <fluent-icon icon="error" size="28" class="text-n-ruby-9" />
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.ERROR') }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.ERROR_DESCRIPTION') }}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          color="slate"
          size="md"
          @click="fetchPipelines"
        >
          {{ t('PIPELINE.MESSAGE_TEMPLATES.RETRY') }}
        </Button>
      </div>

      <div
        v-else-if="!pipelines.length"
        class="flex h-full min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak p-6 text-center"
      >
        <span
          class="flex size-14 items-center justify-center rounded-full bg-n-blue-3 text-n-blue-11"
        >
          <fluent-icon icon="document" size="28" />
        </span>
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.EMPTY') }}
          </h2>
          <p class="mt-1 max-w-md text-sm text-n-slate-11">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.EMPTY_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div v-else class="mx-auto flex w-full max-w-[96rem] flex-col gap-5">
        <section
          :aria-label="t('PIPELINE.MESSAGE_TEMPLATES.FILTERS_LABEL')"
          class="space-y-4"
        >
          <div
            class="grid grid-cols-1 gap-4 md:grid-cols-[minmax(14rem,22rem)_minmax(16rem,1fr)]"
          >
            <div
              class="space-y-1 [&>div]:w-full [&_select]:min-h-11 [&_select]:w-full"
            >
              <label
                for="message-templates-pipeline"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.PIPELINE_FILTER') }}
              </label>
              <Select
                v-model="selectedPipelineId"
                input-id="message-templates-pipeline"
                name="pipeline"
                :options="pipelineOptions"
              />
            </div>

            <div class="space-y-1">
              <label
                for="message-template-search"
                class="text-sm font-medium text-n-slate-12"
              >
                {{ t('PIPELINE.MESSAGE_TEMPLATES.SEARCH_LABEL') }}
              </label>
              <div class="relative">
                <fluent-icon
                  icon="search"
                  size="16"
                  aria-hidden="true"
                  class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-n-slate-10"
                />
                <input
                  id="message-template-search"
                  v-model="searchQuery"
                  name="messageTemplateSearch"
                  type="search"
                  autocomplete="off"
                  :placeholder="
                    t('PIPELINE.MESSAGE_TEMPLATES.SEARCH_PLACEHOLDER')
                  "
                  class="mb-0 min-h-11 w-full rounded-lg border-0 bg-n-alpha-black2 py-2 pl-10 pr-3 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak placeholder:text-n-slate-10 focus:outline-n-brand"
                />
              </div>
            </div>
          </div>

          <div
            role="group"
            :aria-label="t('PIPELINE.MESSAGE_TEMPLATES.CATEGORY_FILTER')"
            class="flex gap-2 overflow-x-auto pb-1"
          >
            <button
              v-for="category in categories"
              :key="category.id"
              type="button"
              class="flex min-h-11 shrink-0 items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :class="
                selectedCategory === category.id
                  ? 'border-n-brand bg-n-blue-3 text-n-blue-11'
                  : 'border-n-weak bg-n-solid-1 text-n-slate-11 hover:bg-n-alpha-2'
              "
              :aria-pressed="selectedCategory === category.id"
              @click="selectedCategory = category.id"
            >
              <fluent-icon :icon="category.icon" size="16" aria-hidden="true" />
              <span>{{ category.label }}</span>
              <span
                class="rounded-full bg-n-alpha-3 px-2 py-0.5 text-xs tabular-nums"
              >
                {{ categoryCount[category.id] || 0 }}
              </span>
            </button>
          </div>
        </section>

        <section
          v-if="filteredTemplates.length"
          :aria-label="t('PIPELINE.MESSAGE_TEMPLATES.RESULTS_LABEL')"
          class="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-3"
        >
          <article
            v-for="template in filteredTemplates"
            :key="getTemplateKey(template)"
            class="overflow-hidden rounded-xl border border-n-weak bg-n-solid-1"
          >
            <header
              class="flex min-h-14 items-center justify-between gap-3 border-b border-n-weak bg-n-alpha-1 px-4 py-2"
            >
              <div class="flex min-w-0 items-center gap-2">
                <span
                  aria-hidden="true"
                  class="size-2.5 shrink-0 rounded-full bg-n-slate-8"
                />
                <span class="truncate text-xs font-medium text-n-slate-11">
                  {{ template.stageName }}
                </span>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <Button
                  type="button"
                  variant="ghost"
                  color="slate"
                  size="md"
                  icon="i-lucide-eye"
                  :aria-label="
                    t('PIPELINE.MESSAGE_TEMPLATES.PREVIEW_NAMED', {
                      name: template.title,
                    })
                  "
                  @click="openPreview(template)"
                />
                <Button
                  v-if="canManagePipeline"
                  type="button"
                  variant="ghost"
                  color="slate"
                  size="md"
                  icon="i-lucide-copy"
                  :is-loading="busyTemplateKey === getTemplateKey(template)"
                  :disabled="Boolean(busyTemplateKey)"
                  :aria-label="
                    t('PIPELINE.MESSAGE_TEMPLATES.DUPLICATE_NAMED', {
                      name: template.title,
                    })
                  "
                  @click="handleDuplicateTemplate(template)"
                />
                <Button
                  v-if="canManagePipeline"
                  type="button"
                  variant="ghost"
                  color="slate"
                  size="md"
                  icon="i-lucide-pencil"
                  :disabled="Boolean(busyTemplateKey)"
                  :aria-label="
                    t('PIPELINE.MESSAGE_TEMPLATES.EDIT_NAMED', {
                      name: template.title,
                    })
                  "
                  @click="handleEditTemplate(template, template.stageId)"
                />
                <Button
                  v-if="canManagePipeline"
                  type="button"
                  variant="ghost"
                  color="ruby"
                  size="md"
                  icon="i-lucide-trash-2"
                  :disabled="Boolean(busyTemplateKey)"
                  :aria-label="
                    t('PIPELINE.MESSAGE_TEMPLATES.DELETE_NAMED', {
                      name: template.title,
                    })
                  "
                  @click="confirmDelete(template)"
                />
              </div>
            </header>

            <div class="space-y-4 p-4">
              <div class="flex items-start gap-3">
                <span
                  class="flex size-10 shrink-0 items-center justify-center rounded-lg"
                  :class="getCategoryColor(template.category)"
                >
                  <fluent-icon
                    :icon="getCategory(template.category).icon"
                    size="20"
                    aria-hidden="true"
                  />
                </span>
                <div class="min-w-0 flex-1">
                  <h2
                    class="line-clamp-1 text-base font-semibold text-n-slate-12"
                  >
                    {{ template.title }}
                  </h2>
                  <p
                    class="mt-1 line-clamp-3 whitespace-pre-wrap text-sm text-n-slate-11"
                  >
                    {{ template.content }}
                  </p>
                </div>
              </div>

              <div
                v-if="template.variables.length"
                class="border-t border-n-weak pt-3"
              >
                <p class="mb-2 text-xs font-medium text-n-slate-11">
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.VARIABLES') }}
                </p>
                <div class="flex flex-wrap gap-1.5">
                  <code
                    v-for="variable in template.variables.slice(0, 3)"
                    :key="variable"
                    class="rounded-md bg-n-violet-3 px-2 py-1 text-xs text-n-violet-11"
                  >
                    {{ variable }}
                  </code>
                  <span
                    v-if="template.variables.length > 3"
                    class="rounded-md bg-n-slate-3 px-2 py-1 text-xs text-n-slate-11"
                    :aria-label="
                      t('PIPELINE.MESSAGE_TEMPLATES.MORE_VARIABLES', {
                        count: template.variables.length - 3,
                      })
                    "
                  >
                    {{
                      t('PIPELINE.MESSAGE_TEMPLATES.MORE_VARIABLES_SHORT', {
                        count: template.variables.length - 3,
                      })
                    }}
                  </span>
                </div>
              </div>

              <div
                v-if="template.conditions?.enabled"
                class="flex flex-wrap gap-2"
              >
                <span
                  v-if="template.conditions?.enabled"
                  class="rounded-md bg-n-teal-3 px-2 py-1 text-xs text-n-teal-11"
                >
                  {{ t('PIPELINE.MESSAGE_TEMPLATES.CONDITIONS') }}
                </span>
              </div>
            </div>
          </article>
        </section>

        <div
          v-else
          class="flex min-h-64 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-n-weak p-6 text-center"
        >
          <fluent-icon icon="search" size="36" class="text-n-slate-9" />
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              {{
                allTemplates.length
                  ? t('PIPELINE.MESSAGE_TEMPLATES.NO_RESULTS')
                  : t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES')
              }}
            </h2>
            <p class="mt-1 text-sm text-n-slate-11">
              {{
                allTemplates.length
                  ? t('PIPELINE.MESSAGE_TEMPLATES.NO_RESULTS_DESCRIPTION')
                  : t('PIPELINE.MESSAGE_TEMPLATES.NO_TEMPLATES_DESCRIPTION')
              }}
            </p>
          </div>
          <Button
            v-if="allTemplates.length"
            type="button"
            variant="outline"
            color="slate"
            size="md"
            @click="clearFilters"
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.CLEAR_FILTERS') }}
          </Button>
          <Button
            v-else-if="canManagePipeline"
            type="button"
            variant="solid"
            color="blue"
            size="md"
            icon="i-lucide-plus"
            @click="openCreateModal"
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.ADD') }}
          </Button>
        </div>
      </div>
    </main>

    <Modal v-model:show="showPreviewModal" size="medium">
      <div class="space-y-5 p-5 sm:p-6">
        <div class="pr-12">
          <h2 data-modal-title class="text-xl font-semibold text-n-slate-12">
            {{ templateToPreview?.title }}
          </h2>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.PREVIEW_DESCRIPTION') }}
          </p>
        </div>

        <dl
          class="grid grid-cols-1 gap-4 rounded-xl bg-n-alpha-1 p-4 sm:grid-cols-3"
        >
          <div>
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.STAGE') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ templateToPreview?.stageName }}
            </dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.CATEGORY') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ getCategory(templateToPreview?.category).label }}
            </dd>
          </div>
          <div>
            <dt class="text-xs font-medium text-n-slate-11">
              {{ t('PIPELINE.MESSAGE_TEMPLATES.CREATED') }}
            </dt>
            <dd class="mt-1 text-sm text-n-slate-12">
              {{ formatDate(templateToPreview?.created_at) }}
            </dd>
          </div>
        </dl>

        <div class="min-h-40 rounded-xl border border-n-weak bg-n-solid-1 p-4">
          <p class="whitespace-pre-wrap text-sm text-n-slate-12">
            {{ templateToPreview?.content }}
          </p>
        </div>

        <div
          class="flex flex-col-reverse gap-2 border-t border-n-weak pt-4 sm:flex-row sm:justify-end"
        >
          <Button
            type="button"
            variant="outline"
            color="slate"
            size="md"
            class="w-full sm:w-auto"
            @click="showPreviewModal = false"
          >
            {{ t('PIPELINE.ACTIONS.CLOSE') }}
          </Button>
          <Button
            v-if="canManagePipeline"
            type="button"
            variant="solid"
            color="blue"
            size="md"
            icon="i-lucide-pencil"
            class="w-full sm:w-auto"
            @click="
              handleEditTemplate(templateToPreview, templateToPreview.stageId);
              showPreviewModal = false;
            "
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.EDIT') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal
      v-if="canManagePipeline"
      v-model:show="showDeleteModal"
      :close-on-backdrop-click="!isDeleting"
      :close-on-escape="!isDeleting"
      :show-close-button="!isDeleting"
      @close="resetDeleteState"
    >
      <div class="space-y-5 p-5 sm:p-6">
        <div class="pr-12">
          <h2 data-modal-title class="text-lg font-semibold text-n-slate-12">
            {{ t('PIPELINE.MESSAGE_TEMPLATES.DELETE') }}
          </h2>
          <p class="mt-2 text-sm text-n-slate-11">
            {{
              t('PIPELINE.MESSAGE_TEMPLATES.DELETE_CONFIRM_MESSAGE', {
                name: templateToDelete?.title || '',
              })
            }}
          </p>
        </div>
        <div class="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            color="slate"
            size="md"
            :disabled="isDeleting"
            class="w-full sm:w-auto"
            @click="resetDeleteState"
          >
            {{ t('PIPELINE.ACTIONS.CANCEL') }}
          </Button>
          <Button
            type="button"
            variant="solid"
            color="ruby"
            size="md"
            :is-loading="isDeleting"
            :disabled="isDeleting"
            class="w-full sm:w-auto"
            @click="handleDeleteTemplate"
          >
            {{ t('PIPELINE.MESSAGE_TEMPLATES.DELETE') }}
          </Button>
        </div>
      </div>
    </Modal>

    <Modal v-if="canManagePipeline" v-model:show="editModalModel" size="large">
      <div class="p-4 sm:p-6">
        <MessageTemplateForm
          ref="editFormRef"
          :initial-data="templateToEdit"
          is-editing
          @dirty-change="editFormDirty = $event"
          @saved="handleTemplateSaved"
          @close="closeEditModal"
        />
      </div>
    </Modal>

    <Modal
      v-if="canManagePipeline"
      v-model:show="createModalModel"
      size="large"
    >
      <div class="p-4 sm:p-6">
        <MessageTemplateForm
          ref="createFormRef"
          :initial-data="newTemplateData"
          :is-editing="false"
          @dirty-change="createFormDirty = $event"
          @saved="handleNewTemplateCreated"
          @close="closeCreateModal"
        />
      </div>
    </Modal>
  </div>
</template>
